import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { TimeframeConditionType, TimeframeUnit } from "./segment-timeframe";

const timeframeUnitSchema = createEnum(TimeframeUnit, {
  enumName: "TimeframeUnit",
});

const afterTimeframeSchema = z.object({
  type: z.literal(TimeframeConditionType.AFTER),
  options: z.object({ date: z.string().date() }),
});

const beforeTimeframeSchema = z.object({
  type: z.literal(TimeframeConditionType.BEFORE),
  options: z.object({ date: z.string().date() }),
});

export const todayTimeframeSchema = z.object({
  type: z.literal(TimeframeConditionType.TODAY),
  options: z.null(),
});

const automationStartTimeframeSchema = z.object({
  type: z.literal(TimeframeConditionType.AUTOMATION_START),
  options: z.object({ automationStartTime: z.string().datetime() }),
});

const allTimeTimeframeSchema = z.object({
  type: z.literal(TimeframeConditionType.ALL_TIME),
  options: z.null(),
});

const betweenRelativeTimeframeSchema = z.object({
  type: z.literal(TimeframeConditionType.BETWEEN_RELATIVE),
  options: z
    .object({ start: z.number(), end: z.number(), units: timeframeUnitSchema })
    .refine(({ start, end }) => start < end, {
      message: "Start value must be less than end value",
      path: ["options"],
    }),
});

const betweenTimeframeSchema = z.object({
  type: z.literal(TimeframeConditionType.BETWEEN_DATES),
  options: z
    .object({ range: z.tuple([z.string().date(), z.string().date()]) })
    .refine(({ range }) => new Date(range[0]) < new Date(range[1]), {
      message: "Start date must be before end date",
      path: ["options"],
    }),
});

const beforeRelativeTimeframeSchema = z.object({
  type: z.literal(TimeframeConditionType.BEFORE_RELATIVE),
  options: z.object({ value: z.number(), units: timeframeUnitSchema }),
});

const beforeNowRelativeTimeframeSchema = z.object({
  type: z.literal(TimeframeConditionType.BEFORE_NOW_RELATIVE),
  options: z.object({ value: z.number(), units: timeframeUnitSchema }),
});

const onTimeframeSchema = z.object({
  type: z.literal(TimeframeConditionType.ON),
  options: z.object({ date: z.string().date() }),
});

export const beforeNowRelativeExactTimeframeSchema = z.object({
  type: z.literal(TimeframeConditionType.BEFORE_NOW_RELATIVE_EXACT),
  options: z.object({ value: z.number(), units: timeframeUnitSchema }),
});

export const afterNowRelativeExactTimeframeSchema = z.object({
  type: z.literal(TimeframeConditionType.AFTER_NOW_RELATIVE_EXACT),
  options: z.object({ value: z.number(), units: timeframeUnitSchema }),
});

const thisWeekTimeframeSchema = z.object({
  type: z.literal(TimeframeConditionType.THIS_WEEK),
  options: z.null(),
});

const thisMonthTimeframeSchema = z.object({
  type: z.literal(TimeframeConditionType.THIS_MONTH),
  options: z.null(),
});

// Combined timeframe schema
const fullDateTimeframeSchema_ = z.union([
  afterTimeframeSchema,
  beforeTimeframeSchema,
  allTimeTimeframeSchema,
  betweenRelativeTimeframeSchema,
  betweenTimeframeSchema,
  beforeRelativeTimeframeSchema,
  beforeNowRelativeTimeframeSchema,
  onTimeframeSchema,
  todayTimeframeSchema,
  automationStartTimeframeSchema,
]);
export const fullDateTimeframeSchema: z.ZodType<FullDateTimeframe> =
  fullDateTimeframeSchema_;
export type FullDateTimeframe = z.infer<typeof fullDateTimeframeSchema_>;

const annualDateTimeframeSchema_ = z.union([
  beforeNowRelativeTimeframeSchema,
  afterTimeframeSchema,
  beforeTimeframeSchema,
  betweenTimeframeSchema,
  onTimeframeSchema,
  todayTimeframeSchema,
  beforeNowRelativeExactTimeframeSchema,
  afterNowRelativeExactTimeframeSchema,
  thisWeekTimeframeSchema,
  thisMonthTimeframeSchema,
]);
export const annualDateTimeframeSchema: z.ZodType<AnnualDateTimeframe> =
  annualDateTimeframeSchema_;
export type AnnualDateTimeframe = z.infer<typeof annualDateTimeframeSchema_>;
